import ModelContextProtocolSVG from '@/assets/svg/model-context-protocol.svg?react';
import { ApiMethodIcon } from '@/components/api-method-icon/ApiMethodIcon';
import { ApiMethodIconSize } from '@/components/api-method-icon/type/ApiMethodIconSize';
import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { CopyableText } from '@/components/copyable-text/CopyableText';
import { useCalcNavbarHeight } from '@/hooks/useCalcNavbarHeight';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { ScreenSize } from '@/shared/constant/ScreenSize';
import { getRequestBaseUrl } from '@/shared/util/requestBaseUrl';
import { ModelType } from '@tarslib/types/lib/common';
import type { RequestMethodType } from '@tarslib/types/lib/httpApi';
import { Button } from 'aui';
import { FoxIconPlay, FoxIconPlay02 } from 'aui-biz-icons';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useVariablesFromUrl } from '../environment-modal/hooks/useVariablesFromUrl';
import { VibeCodingViaMCPModal } from '../vibe-coding-via-mcp-modal/VibeCodingViaMCPModal';
import { EnvironmentSelect } from './components/environment-select/EnvironmentSelect';

export type EnvironmentUrlProps = {
  value:
    | {
        type: ModelType.HttpApi;
        method: RequestMethodType;
        path: string;
      }
    | {
        type: ModelType.CUSTOM_ENDPOINT;
        protocol: string;
        path: string;
      }
    | {
        type: ModelType.WebSocketApi;
        path: string;
      };

  handleRunInApp?: () => void;
  handleTryItOut?: () => void;
  hasRightSamplePadding?: boolean;

  className?: string;
};

export function EnvironmentUrl(props: EnvironmentUrlProps) {
  const { value, handleRunInApp, handleTryItOut, className, hasRightSamplePadding } = props;

  const navbarHeight = useCalcNavbarHeight();
  const isMobileSize = useAppearanceConfigModelStore((x) => x.screenSize === ScreenSize.Mobile);
  const isOpenDebugPanel = useAppearanceConfigModelStore((x) => x.isOpenDebugPanel);

  const enableVibeCodingViaMCP = useDocsDataModelStore(
    (x) => x.docsBaseConfig.enableVibeCodingViaMCP,
  );
  const hideTryItOutButtonConfig = useDocsDataModelStore(
    (x) => x.docsBaseConfig.hideTryItOutButton,
  );
  const hideRunInAppButtonConfig = useDocsDataModelStore(
    (x) => x.docsBaseConfig.hideRunInAppButton,
  );
  const hideTryItOutButton = React.useMemo(() => {
    return value.type !== ModelType.HttpApi || hideTryItOutButtonConfig || isOpenDebugPanel;
  }, [value.type, hideTryItOutButtonConfig, isOpenDebugPanel]);

  const hideRunInAppButton = React.useMemo(() => {
    return value.type !== ModelType.HttpApi || hideRunInAppButtonConfig;
  }, [value.type, hideRunInAppButtonConfig]);
  const visibilitySettings = useDocsDataModelStore((x) => x.docsBaseConfig.visibilitySettings);
  const showBaseURL = useDocsDataModelStore((x) => x.docsBaseConfig.isShowPrefixUrl);
  const environmentList = useDocsDataModelStore((x) => x.environments.environmentList);
  const selectedEnvironment = useDocsDataModelStore((x) => x.environments.selectedEnvironment);
  const servers = useDocsDataModelStore((x) => x.environments.servers);
  const serverId = useDocsDataModelStore(
    (x) => (x.resourceData.extraData as ResourceHttpApiData['extraData']).serverId,
  );
  const hasBaseUrl = [ModelType.HttpApi].includes(value.type);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const APP_NAME = useEnvConfigModelStore((x) => x.APP_NAME);
  const [vibeCodingViaMCPModal, setVibeCodingViaMCPModalVisible] = useState(false);

  const hasEnvironmentSelect = React.useMemo(() => {
    return value.type === ModelType.HttpApi && environmentList.length > 1;
  }, [value, environmentList.length]);

  const baseUrl = React.useMemo(() => {
    if (!hasBaseUrl) {
      return '';
    }
    return getRequestBaseUrl({
      serverId,
      environment: selectedEnvironment,
      servers: servers || [],
      path: value.path,
    });
  }, [value, servers, selectedEnvironment, serverId, hasBaseUrl]);

  const fullPath = React.useMemo(() => {
    return showBaseURL ? `${baseUrl}${value.path}` : value.path;
  }, [showBaseURL, baseUrl, value.path]);

  // load client environment variables and merge with environment variables in URL
  useVariablesFromUrl();

  const showVibeCodingViaMCPBtn = React.useMemo(() => {
    return value.type === ModelType.HttpApi && enableVibeCodingViaMCP;
  }, [value.type, enableVibeCodingViaMCP]);
  return (
    <>
      {hasEnvironmentSelect && (
        <EnvironmentSelect
          className={clsx('flex items-center py-1', {
            ['@[1000px]/http-api-content:hidden']: !hasRightSamplePadding,
            ['@[1440px]/http-api-content:hidden']: hasRightSamplePadding,
          })}
          selectClassName="max-w-full text-sm font-400 text-input"
          size="small"
          variant="text"
        />
      )}

      <ContainerQuery
        containerSizes={[500, 998]}
        className={clsx(
          '@container/environment-url',
          'z-10 flex items-stretch rounded-xl border border-border-color-base bg-input shadow-sm',
          {
            ['sticky']: !isMobileSize,
          },
          className,
        )}
        style={{
          top: isMobileSize ? undefined : navbarHeight + 8,
        }}
      >
        {hasEnvironmentSelect && (
          <EnvironmentSelect
            className={clsx(
              'hidden items-center border-r border-border-color-base @[998px]/environment-url:flex',
            )}
            selectClassName="max-w-[200px] text-lg font-base text-secondary"
            bordered={false}
            size="large"
          />
        )}
        <div className={clsx('flex w-0 flex-1 items-stretch p-1')}>
          {value.type !== ModelType.WebSocketApi && (
            <div className="m-1 flex items-center">
              <ApiMethodIcon
                value={value}
                className="flex h-full max-h-[90px]"
                size={ApiMethodIconSize.Large}
              />
            </div>
          )}

          <div className="mx-1 flex min-h-[32px] w-0 flex-1 items-center break-all text-lg font-400 leading-5 text-color">
            <CopyableText
              text={fullPath}
              content={
                <>
                  {showBaseURL && baseUrl && <span className="text-secondary">{baseUrl}</span>}
                  <span>{value.path}</span>
                </>
              }
            />
          </div>
          {(!hideTryItOutButton || !hideRunInAppButton) && (
            <div className="ml-3 flex items-center gap-1">
              {!hideTryItOutButton && (
                <Button
                  type="primary"
                  size="middle"
                  className={clsx(
                    'h-full max-h-14 animate-none transition-none @[998px]/environment-url:h-auto',
                  )}
                  icon={<FoxIconPlay size={14} />}
                  onClick={handleTryItOut}
                  aria-label={clientI18n('tryItOut')}
                >
                  <span className="hidden @[500px]/environment-url:inline-block">
                    {clientI18n('tryItOut')}
                  </span>
                </Button>
              )}

              {!hideRunInAppButton && (
                <Button
                  type="default"
                  size="middle"
                  className={clsx(
                    'hidden animate-none transition-none @[998px]/environment-url:flex',
                  )}
                  icon={<FoxIconPlay02 size={14} />}
                  onClick={handleRunInApp}
                >
                  {clientI18n('runInApp', {
                    appName: APP_NAME,
                  })}
                </Button>
              )}
            </div>
          )}
        </div>
      </ContainerQuery>

      {!hideRunInAppButton && (
        <Button
          type="soft-secondary"
          size="small"
          className={clsx('mr-2 mt-2 animate-none transition-none', {
            ['@[1000px]/http-api-content:hidden']: !hasRightSamplePadding,
            ['@[1440px]/http-api-content:hidden']: hasRightSamplePadding,
          })}
          icon={<FoxIconPlay02 size={12} />}
          onClick={handleRunInApp}
        >
          {clientI18n('runInApp', {
            appName: APP_NAME,
          })}
        </Button>
      )}
      {showVibeCodingViaMCPBtn && (
        <Button
          type="soft-secondary"
          size="small"
          className={clsx('mt-2 animate-none transition-none')}
          icon={
            <span className="appicon">
              <ModelContextProtocolSVG />
            </span>
          }
          onClick={() => setVibeCodingViaMCPModalVisible(true)}
        >
          {clientI18n('docsSite.enableVibeCodingViaMCP')}
        </Button>
      )}
      {vibeCodingViaMCPModal && (
        <VibeCodingViaMCPModal
          visible={vibeCodingViaMCPModal}
          onCancel={() => setVibeCodingViaMCPModalVisible(false)}
        />
      )}
    </>
  );
}
